import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { getJson } from "@/lib/api";
import { locDoc, useLang } from "@/lib/i18n";
import { Filters } from "@/components/catalog/Filters";
import { Flipbook } from "@/components/catalog/Flipbook";
import { CoverPage, EndPage, ItemsPage } from "@/components/catalog/BookPages";
import { ItemSheet } from "@/components/catalog/ItemSheet";

const PER_PAGE = 4;
const KEYS = ["categoria", "marca", "decada", "q"];

const matches = (it, f) =>
  (!f.categoria || it.category === f.categoria) &&
  (!f.marca || it.manufacturer === f.marca) &&
  (!f.decada || String(it.decade) === f.decada) &&
  (!f.q || it.name.toLowerCase().includes(f.q.toLowerCase()));

const subtitleFor = (f, t) =>
  [f.categoria && t(`cat.${f.categoria}`), f.marca, f.decada && t("book.decade", { d: f.decada.slice(2) }), f.q && `“${f.q}”`].filter(Boolean).join(" · ") || t("book.all");

const useCatalogData = () => {
  const [items, setItems] = useState(null);
  const [facets, setFacets] = useState(null);
  const [brands, setBrands] = useState({});
  useEffect(() => {
    getJson("/catalog/items").then((d) => setItems(Array.isArray(d) ? d : []));
    getJson("/catalog/facets").then(setFacets);
    getJson("/catalog/brands").then((d) => setBrands(Object.fromEntries((Array.isArray(d) ? d : []).map((b) => [b.name, b.logo_url]))));
  }, []);
  return { items, facets, brands };
};

export default function CatalogBook() {
  const { items: rawItems, facets, brands } = useCatalogData();
  const { t, lang } = useLang();
  const items = useMemo(() => rawItems && rawItems.map((it) => locDoc(it, lang)), [rawItems, lang]);
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(null);
  const f = Object.fromEntries(KEYS.map((k) => [k, params.get(k)]));
  const set = (k, v) => {
    const next = new URLSearchParams(k === "__clear" ? "" : params);
    if (k !== "__clear") (v ? next.set(k, v) : next.delete(k));
    setParams(next, { replace: true });
  };
  const filtered = useMemo(() => (items ?? []).filter((it) => matches(it, f)), [items, f.categoria, f.marca, f.decada, f.q]); // eslint-disable-line react-hooks/exhaustive-deps
  const chunks = useMemo(() => Array.from({ length: Math.ceil(filtered.length / PER_PAGE) }, (_, i) => filtered.slice(i * PER_PAGE, (i + 1) * PER_PAGE)), [filtered]);
  const count = (chunks.length + 2) % 2 ? chunks.length + 3 : chunks.length + 2;
  const renderPage = (i) => {
    if (i === 0) return <CoverPage total={filtered.length} subtitle={subtitleFor(f, t)} />;
    if (i <= chunks.length) return <ItemsPage no={i + 1} items={chunks[i - 1]} onOpen={setOpen} />;
    if (i === chunks.length + 1) return <EndPage no={i + 1} empty={!filtered.length} />;
    return <div className="bk-page bk-page-blank" />;
  };
  const filterUi = <Filters facets={facets} value={f} set={set} />;

  return (
    <main className="px-5 pb-24 pt-28 md:px-10" data-testid="catalog-book-page">
      <div className="mx-auto max-w-[1440px]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/catalogo" className="mono inline-flex items-center gap-2 text-[11px] text-[var(--muted)] hover:text-[var(--ink)]" data-testid="catalog-back-link"><ArrowLeft size={14} /> {t("book.back")}</Link>
          <Sheet>
            <SheetTrigger asChild>
              <button type="button" className="btn-ghost !py-2 lg:hidden" data-testid="catalog-filters-toggle"><SlidersHorizontal size={15} /> {t("book.filters")}</button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] overflow-y-auto border-white/10 bg-[#0d0c0a] text-[#efe8dc]" data-lenis-prevent>
              <SheetTitle className="font-display mb-6 text-3xl font-normal text-[#efe8dc]">{t("book.filters")}</SheetTitle>
              {filterUi}
            </SheetContent>
          </Sheet>
        </div>
        <div className="mt-6 grid gap-10 lg:grid-cols-[230px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2" data-lenis-prevent>{filterUi}</div>
          </aside>
          <section>
            {items === null ? (
              <Loader2 className="mx-auto mt-24 animate-spin" />
            ) : (
              <Flipbook key={KEYS.map((k) => f[k]).join("|") + lang} count={count} renderPage={renderPage} />
            )}
          </section>
        </div>
      </div>
      <ItemSheet item={open && locDoc(open, lang)} brands={brands} onClose={() => setOpen(null)} />
    </main>
  );
}
